import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import Textarea from 'react-textarea-autosize';
import { HASHTAG_PATTERN_REGEX } from 'mastodon/utils/hashtags';

export function hashtagSegments(value, selection = null) {
  const pattern = new RegExp(HASHTAG_PATTERN_REGEX.source, `${HASHTAG_PATTERN_REGEX.flags}g`);
  const parts = [];
  let previous = 0;
  // Match Mastodon's own hashtag syntax; URL fragments are not hashtags.
  const urls = [...value.matchAll(/https?:\/\/\S+/gi)].map(match => [match.index, match.index + match[0].length]);
  for (const match of value.matchAll(pattern)) {
    const start = match.index + match[0].indexOf('#');
    const end = match.index + match[0].length;
    if (urls.some(([a, b]) => start >= a && start < b)) continue;
    const editing = selection && selection.start <= end && selection.end >= start;
    parts.push({ text: value.slice(previous, start), highlighted: false });
    parts.push({ text: value.slice(start, end), highlighted: !editing });
    previous = end;
  }
  parts.push({ text: value.slice(previous), highlighted: false });
  return parts;
}

// Keep the native textarea: autocomplete, undo, selection, IME and accessibility
// still operate on the original input, which always paints its own text. The
// non-interactive mirror only tints completed hashtags, never the whole draft.
export const HighlightedTextarea = forwardRef(({ highlightHashtags, ...props }, forwardedRef) => {
  const input = useRef(null);
  const mirror = useRef(null);
  const [selection, setSelection] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const updateSelection = useCallback((event) => {
    const element = event.currentTarget;
    setSelection(document.activeElement === element ? { start: element.selectionStart, end: element.selectionEnd } : null);
  }, []);
  const syncScroll = useCallback(() => {
    if (mirror.current && input.current) {
      mirror.current.scrollTop = input.current.scrollTop;
      mirror.current.scrollLeft = input.current.scrollLeft;
    }
  }, []);
  const setRef = useCallback(element => {
    input.current = element;
    if (typeof forwardedRef === 'function') forwardedRef(element);
    else if (forwardedRef) forwardedRef.current = element;
  }, [forwardedRef]);

  useLayoutEffect(() => {
    if (!highlightHashtags || !input.current) return;
    const element = input.current;
    const measure = () => {
      const computed = getComputedStyle(element);
      const style = {};
      // Copy browser text shaping/wrapping defaults too. Rounded offsetWidth
      // values can wrap a word differently from the native textarea.
      for (const key of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontStretch', 'fontKerning', 'fontFeatureSettings', 'fontVariationSettings', 'fontVariantLigatures', 'textRendering', 'lineHeight', 'letterSpacing', 'wordSpacing', 'whiteSpace', 'wordBreak', 'overflowWrap', 'textAlign', 'textIndent', 'textTransform', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'tabSize', 'direction', 'boxSizing', 'width', 'height']) style[key] = computed[key];
      setGeometry({ ...style, left: element.offsetLeft, top: element.offsetTop });
      setSelection(document.activeElement === element ? { start: element.selectionStart, end: element.selectionEnd } : null);
      syncScroll();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [highlightHashtags, props.value, syncScroll]);

  const segments = hashtagSegments(props.value ?? '', selection);
  // Ordinary drafts should use native visible text, not the highlighting layer.
  const ready = highlightHashtags && geometry && segments.some(part => part.highlighted);
  useLayoutEffect(syncScroll, [geometry, props.value, ready, syncScroll]);

  return <>
    {ready && <div ref={mirror} aria-hidden='true' dir='auto' className='dr-textarea-mirror' style={geometry}>
      {segments.map((part, index) => part.highlighted ? <span key={index} className='dr-textarea-hashtag'>{part.text}</span> : part.text)}
      {'\u200b'}
    </div>}
    <Textarea {...props} ref={setRef} className={`${props.className ?? ''}${ready ? ' dr-textarea-highlighted' : ''}`}
      onChange={event => { updateSelection(event); props.onChange?.(event); }}
      onSelect={event => { updateSelection(event); props.onSelect?.(event); }}
      onFocus={event => { updateSelection(event); props.onFocus?.(event); }}
      onBlur={event => { setSelection(null); props.onBlur?.(event); }}
      onKeyUp={event => { updateSelection(event); props.onKeyUp?.(event); }}
      onScroll={event => { syncScroll(); props.onScroll?.(event); }} />
  </>;
});
